// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <div class="detail-view">
    <v-container fluid grid-list-md>
      <v-card>
        <v-toolbar flat>
          <v-btn icon @click="exit" v-if="!noPathId"><v-icon>clear</v-icon></v-btn>
          <v-spacer></v-spacer>
          <template v-for="(button, i) in buttons">
            <v-btn v-if="canModifyNoDisable" light @click="button.function(item)" :color="button.color" :key="i">{{button.text}}</v-btn>
          </template>
          <v-btn class="mx-1" v-if="canModify" light color="success" @click="saveItem(false)">Save</v-btn>
          <v-btn class="mx-1" v-if="canDelete" light color="error" @click.stop="showDeleteDialog = true">Delete</v-btn>
        </v-toolbar>
        <v-form v-model="formValid" ref="form">
        <v-tabs color="grey" centered slider-color="yellow" v-model="tab">
          <v-tab v-for="(tab, i) in values" :key="i" :href="`#tab-${i}`">{{ tab.name }}</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item v-for="(tab, i) in values" :key="i" :value="`tab-${i}`">
            <v-card-text>
              <template v-for="(row, i) in values[i].items">
                <v-layout row wrap :key="i">
                  <template v-for="(value, j) in row">
                    <v-flex :class="value.width || itemWidth" v-if="value.type=='checkbox'" align-end :key="j"
                    style="rgba(0,0,0,.50);"
                    >
                      {{ value.name }}
                      <v-checkbox
                      :value="getElement(item, value.value)"
                      @change="(val) => changeElement(item, value.value, val)"
                      hide-details
                      :disabled="value.disabled || !canModify"
                      style="margin:0px;"
                      ></v-checkbox>
                    </v-flex>
                    <v-flex :class="value.width || itemWidth" v-else-if="value.type=='select'" :key="j">
                      <v-autocomplete
                        clearable
                        :label="value.name"
                        :item-text="value.itemText"
                        :item-value="value.itemValue"
                        :return-object="value.returnObject"
                        v-bind:items="contextData[value.context]"
                        :value="getElement(item, value.value)"
                        @input="(val) => changeElement(item, value.value, val)"
                        :multiple="value.multiple"
                        :chips="value.multiple"
                        deletable-chips
                        :rules="rulesInput(value.required, value.rule)"
                        :small-chips="value.multiple"
                        :disabled="value.disabled || !canModify"
                        hide-details
                        :required="value.required"
                      ></v-autocomplete>
                    </v-flex>
                    <v-flex :class="value.width || itemWidth" v-else-if="value.type=='combobox'" :key="j">
                      <v-combobox
                        clearable
                        :label="value.name"
                        :item-text="value.itemText"
                        :item-value="value.itemValue"
                        :return-object="value.returnObject"
                        v-bind:items="contextData[value.context]"
                        :value="getElement(item, value.value)"
                        @input="(val) => changeElement(item, value.value, val)"
                        :multiple="value.multiple"
                        :chips="value.multiple"
                        deletable-chips
                        :rules="rulesInput(value.required, value.rule)"
                        :disabled="value.disabled || !canModify"
                        hide-details
                        :required="value.required"
                      ></v-combobox>
                    </v-flex>
                    <v-flex :class="value.width || itemWidth" v-else-if="value.type=='date'" :key="j">
                      <date-view
                      v-model="item[value.value]"
                      :disabled="value.disabled || !canModify"
                      :label="value.name"
                      ></date-view>
                    </v-flex>
                    <v-flex :class="value.width || itemWidth" v-else-if="value.type=='duration'" :key="j">
                      <duration-view
                      :value="getElement(item, value.value)"
                      @input="(val) => changeElement(item, value.value, val)"
                      :disabled="value.disabled || !canModify"
                      :label="value.name"
                      :unit="value.valueType"
                      ></duration-view>
                    </v-flex>
                    <v-flex xs12 v-else-if="value.type=='itemList'" :key="j">
                      <item-list
                        :allowAdd="value.allowAdd"
                        :allowDelete="value.allowDelete"
                        :context="value.context"
                        :editRoute="value.editRoute"
                        :endpoint="value.value"
                        :filter="populateFilters(value.filter, item)"
                        :headers="value.headers"
                        :id="value.id"
                        :loadData="dataLoaded"
                        :model="value.model"
                        :name="value.name"
                        :pagination="value.pagination"
                        :permissions="value.permissions"
                        v-model="item[value.value]"
                      ></item-list>
                    </v-flex>
                    <v-flex xs12 v-else-if="value.type==='expansionView'" :key="j">
                      <expansion-view
                        :endpoint="value.endpoint"
                        :site="value.site">
                      </expansion-view>
                    </v-flex>
                    <v-flex xs12 v-else-if="value.type==='component'" :key="j">
                      <component
                      :is="value.component"
                      :prefix="value.prefix"
                      :value="getElement(item, value.value)"
                      @input="(val) => changeElement(item, value.value, val)"
                      v-on:change="saveItem(true)"
                      ></component>
                    </v-flex>
                    <v-flex :class="value.width || itemWidth" v-else-if="value.type==='formatTime'" :key="j">
                      <v-text-field
                        :label="value.name"
                        :value="getTimeElement(item, value.value)"
                        @change="(val) => changeElement(item, value.value, val, value.valueType)"
                        :required="value.required"
                        :disabled="value.disabled || !canModify"
                        :rules="rulesInput(value.required, value.rule)"
                        hide-details
                      ></v-text-field>
                    </v-flex>
                    <v-flex :class="value.width || itemWidth" v-else :key="j">
                      <v-text-field
                        :label="value.name"
                        :value="getElement(item, value.value)"
                        @change="(val) => changeElement(item, value.value, val, value.valueType)"
                        :required="value.required"
                        :disabled="value.disabled || !canModify"
                        :rules="rulesInput(value.required, value.rule)"
                        hide-details
                      ></v-text-field>
                    </v-flex>
                  </template>
                </v-layout>
                <v-divider :key="i + 10000"></v-divider>
                <br :key="i + 20000">
                <v-divider :key="i + 30000"></v-divider>
              </template>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
        </v-form>
      </v-card>
    </v-container>

    <v-dialog v-model="showDeleteDialog" max-width="300px">
      <v-card>
        <v-card-title>
          <div class="headline">Delete?</div>
        </v-card-title>
        <v-card-text>Are you sure you want to delete this {{ name }}?  This action cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click.native="showDeleteDialog = false">Cancel</v-btn>
          <v-btn text color="red" @click.native="deleteItem">I'm Sure</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <error-snackbar v-model="errorText"></error-snackbar>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import DateView from './DateView'
  import DurationView from './DurationView'
  import ErrorSnackbar from 'vue-common/components/ErrorSnackbar'
  import ItemList from 'vue-common/components/ItemList.vue'
  import ExpansionView from './ExpansionView.vue'
  import moment from 'moment'

  import {AXIOSAPI, getItems} from 'vue-common/components/requests-common'
  import {checkPermission} from 'vue-common/components/user'
  import {PERMISSIONS} from 'vue-common/components/constants'

  export default {
    name: 'detail-view',

    components: {
      DateView,
      DurationView,
      ErrorSnackbar,
      ExpansionView,
      ItemList
    },

    data () {
      return {
        contextData: [],
        dataLoaded: false,
        errorText: '',
        formValid: true,
        item: {},
        showDeleteDialog: false,
        tab: 'tab-0'
      }
    },

    props: {
      // a list of buttons that show up before the save/delete buttons
      // { text: "button name", functon: "button click function", color: "button color" }
      buttons: {
        type: Array,
        default: () => []
      },
      // function that is called after creating a new object
      callbackCreate: Function,
      // disables modifying objects
      disableModify: Boolean,
      endpoint: String,
      // A list of object properties that should be set to a given value.
      hiddenValues: Array,
      parentRoute: String,
      // The access rule that is checked for permissions
      permissions: String,
      name: String,
      // noPathId - allows using detailview with urls without an id
      noPathId: Boolean,
      // reload the data when toggled
      refresh: Boolean,
      // v-model definition
      value: {
        type: Object,
        default: () => {}
      },
      // Object describing the tabs and inputs.
      values: Array,
      // Standard width of the fields
      itemWidth: {
        type: String,
        default: 'xs12 md4'
      }
    },

    created () {
      Promise.all(this.loadContext()).then(() => {
        if (!this.isNew) {
          this.getData()
        } else {
          if (this.value) {
            this.item = this.value
          } else {
            this.item = {}
          }
        }
      })
    },

    watch: {
      // Enable parent access to the model
      item () {
        this.updateHiddenValues()
        this.$emit('input', this.item)
      },

      value () {
        this.item = this.value
      },

      refresh () {
        this.getData()
      }
    },

    computed: {
      ...mapGetters([
        'accessRules'
      ]),

      canDelete: function () {
        return checkPermission(this.accessRules, this.$props.permissions, PERMISSIONS.DELETE) && !this.isNew && !this.$props.noPathId
      },

      canModifyNoDisable: function () {
        return checkPermission(this.accessRules, this.$props.permissions, PERMISSIONS.MODIFY)
      },

      canModify: function () {
        return this.canModifyNoDisable && (!this.$props.disableModify || this.isNew)
      },

      isNew: function () {
        return this.$route.params.id === 'new'
      },

      url: function () {
        if (this.$props.noPathId) {
          return this.$props.endpoint
        }
        return this.$props.endpoint + '/' + this.$route.params.id
      },

      context: function () {
        var context = []
        for (var k in this.$props.values) {
          for (var j in this.$props.values[k].items) {
            for (var i in this.$props.values[k].items[j]) {
              var contextPath = this.$props.values[k].items[j][i].context
              if (contextPath) {
                if (!context.find(el => el === contextPath)) {
                  context.push(contextPath)
                }
              }
            }
          }
        }
        return context
      }
    },

    methods: {
      deleteItem: function () {
        AXIOSAPI.delete(this.url).then(response => {
          this.exit()
        }).catch(e => {
          this.errorText = 'Delete failed: ' + e.response.data
          console.log(e.response)
        })
        this.showDeleteDialog = false
      },

      changeElement: function (item, path, value, type) {
        var splitPath = path.split('.')
        var obj = item
        for (var i = 0; i < splitPath.length - 1; i++) {
          var pathSeg = splitPath[i]
          if (pathSeg in obj) {
            obj = obj[pathSeg]
          } else {
            obj[pathSeg] = {}
            obj = obj[pathSeg]
          }
        }
        if (type === 'number') {
          value = parseFloat(value)
        }
        this.$set(obj, splitPath[splitPath.length - 1], value)
      },

      exit: function () {
        if (!this.noPathId) {
          if (this.$props.parentRoute != "" && this.$props.parentRoute !== undefined) {
            this.$router.push({name: this.$props.parentRoute})
          } else {
            this.$router.go(-1)
          }
        }
      },

      getData: function () {
        this.dataLoaded = false
        getItems(this, 'item', this.url, {}, () => {
          this.dataLoaded = true
        })
      },

      getElement: function (item, element) {
        var path = element.split('.')
        var obj = item
        while (path.length) {
          if (obj) {
            obj = obj[path.shift()]
          } else {
            return ''
          }
        }
        return obj
      },

      getTimeElement: function (item, element) {
        var path = element.split('.')
        var obj = item
        while (path.length) {
          if (obj) {
            obj = obj[path.shift()]
          } else {
            return ''
          }
        }
        return this.formatTime(obj)
      },

      formatTime: function (time) {
        var m = moment(time)
        if (m.isBefore('1000-01-01')) {
          return 'N/A'
        }
        return m.format('YYYY-MM-DD' + ' hh:mm:ss a')
      },

      loadContext: function () {
        // return a list of promises so we can wait until this function returns to do other stuff
        var dostuff = function (context, location, response) {
          context.$set(context.contextData, location, response.data)
        }

        var list = []
        this.context.forEach((arrayElement) => {
          var prom = AXIOSAPI.get(arrayElement).then(response => {
            dostuff(this, arrayElement, response)
          })
            .catch(e => {
              this.errorText = 'Failed to load ' + arrayElement + ': ' + e.response.data
              console.log(e.response)
            })
          list.push(prom)
        })
        return list
      },

      populateFilters: function (filters, item) {
        var list = []
        for (var i in filters) {
          var filter = {
            name: filters[i].name,
            value: this.getElement(this.item, filters[i].value)
          }
          list.push(filter)
        }
        return list
      },

      saveItem: function (noExit) {
        this.updateHiddenValues()
        if (this.verifyItem()) {
          if (this.isNew) {
            var query1 = this.$props.endpoint
            AXIOSAPI.post(query1, this.item).then(response => {
              if (this.$props.callbackCreate) {
                this.$props.callbackCreate(response.data).then(x => {
                  if (!noExit) {
                    this.exit()
                  }
                  this.errorText = 'Saved ' + this.$props.name
                })
              } else {
                if (!noExit) {
                  this.exit()
                }
                this.errorText = 'Saved ' + this.$props.name
              }
            }).catch(e => {
              this.errorText = 'Failed to save ' + this.$props.name + ': ' + e.response.data
            })
          } else {
            AXIOSAPI.put(this.url, this.item).then(response => {
              if (!noExit) {
                this.exit()
              }
              this.errorText = 'Saved ' + this.$props.name
            }).catch(e => {
              this.errorText = 'Failed to save ' + this.$props.name + ': ' + e.response.data
              console.log(e.response)
            })
          }
        }
      },

      updateHiddenValues: function () {
        for (var i in this.$props.hiddenValues) {
          this.changeElement(this.item, this.$props.hiddenValues[i].path, this.$props.hiddenValues[i].value)
        }
      },

      verifyItem: function () {
        this.$refs.form.validate()
        // This is required to generate the error snackbar
        for (var i in this.values) {
          var tab = this.values[i]
          if (tab.name !== 'Zone History') {
            for (var j in tab.items) {
              var row = tab.items[j]
              for (var k in row) {
                var def = row[k]
                var thisValue = this.getElement(this.item, def.value)
                if (def.required) {
                  if (!this.requiredCheck(thisValue)) {
                    this.errorText = def.name + ' in the ' + tab.name + ' tab is a required field.'
                    return false
                  }
                }
                if (typeof def.rule === 'function') {
                  var res = def.rule(thisValue)
                  if (res !== true) {
                    this.errorText = def.name + ' in the ' + tab.name + ' tab: ' + res
                    return false
                  }
                }
              }
            }
          }
        }
        return true
      },

      requiredCheck: function (value) {
        if (value !== null && value !== '' && value !== [] && value !== undefined) {
          return true
        }
        return false
      },

      rulesInput: function (required, rule) {
        var list = []
        if (required) {
          list.push(this.ruleRequired)
        }
        if (typeof rule === 'function') {
          list.push(rule)
        }
        return list
      },

      ruleRequired: function (value) {
        if (this.requiredCheck(value)) {
          return true
        } else {
          return 'Please select an item.'
        }
      }
    }
  }
</script>

<style>
</style>



// WEBPACK FOOTER //
// DetailView.vue?761d0c09